import React from 'react'
import { Card } from '@common/material'
import {
  AuditResult,
  AuditResultScopeTypes,
} from '@core/entities/audit/AuditResult'
import { localizedStrings } from '@core/strings'
import { RegisterItem } from '@core/entities/register/RegisterItem'
import {
  TimelineBriefingParticipantBlock,
  TimelineBriefingSessionBlock,
  TimelineCheckInBlock,
  TimelineChecklistCommentBlocks,
  TimelineCompanyBlock,
  TimelineCompanyDiffBlock,
  TimelineComplianceEventBlock,
  TimelineDiffBlock,
  TimelineEventBlock,
  TimelineEventHeader,
  TimelineGroupPermissionsBlocks,
  TimelineLinkBlock,
  TimelineMediaBlock,
  TimelineMoveToFolderBlock,
  TimelineMultiLinksBlock,
  TimelineOnboardingFormDiffBlock,
  TimelineOrganisationFeatureFlagsBlock,
  TimelinePermissionAssignmentsBlocks,
  TimelinePermitBlocks,
  TimelinePermitLocationBlock,
  TimelinePermitStatusTrailBlock,
  TimelineProjectStatusTrailBlock,
  TimelineProjectUpdatesBlock,
  TimelineRegisterPropertiesBlocks,
  TimelineSignatureBlock,
  TimelineSiteBoxDocumentUpdateBlock,
  TimelineSiteBoxStatusTrailBlock,
  TimelineTaskStepQuestionsBlock,
  TimelineTemplateBlocks,
  TimelineTextBlock,
  TimelineUserBlocks,
  TimelineUserRoleUpdateBlock,
} from '@common/Timeline/blocks'

import { PageUrls } from '@core/page-urls'
import { OrganisationUser, ProjectUser } from '@core/entities/user'
import { Collapsible } from '@common/Collapsible'
import { CardProps, useTheme } from '@mui/material'
import capitalize from 'lodash/capitalize'

type TimelineItemProps = CardProps & {
  auditResult: AuditResult
  auditResults: Array<AuditResult>
  registerItem?: RegisterItem
  hovered?: boolean
  scope?: AuditResultScopeTypes
}

export const TimelineItem: React.FC<TimelineItemProps> = ({
  auditResult,
  hovered,
  scope,
  auditResults,
  ...props
}) => {
  const theme: any = useTheme()
  const showProjectInfo =
    auditResult?.project &&
    ![
      'PERMIT',
      'CHECKLIST',
      'PROJECT',
      'REGISTER_ITEM',
      'MAP_MODAL',
      'TASK',
    ].includes(scope)
  const showChecklistInfo = auditResult.checklist && scope !== 'CHECKLIST'
  const showPermitInfo =
    !!auditResult.permit && !['PERMIT', 'MAP_MODAL'].includes(scope)
  const showRegisterInfo = !!auditResult.register && scope !== 'REGISTER_ITEM'

  const showAppliedToGroups =
    !!auditResult.appliedToGroups && scope !== 'AUTH_GROUP'

  const draftName =
    auditResult.permitDraft?.draftName || auditResult.checklistDraft?.draftName
  const draftTemplateName =
    auditResult.permitDraft?.templateName ||
    auditResult.checklistDraft?.templateName

  return (
    <Card
      sx={{
        p: 2,
        mb: 2,
        outline: `${
          hovered ? theme.palette.primary.main : 'transparent'
        } solid 1px`,
        borderColor: hovered && theme.palette.primary.main,
        transition: 'outline 0.2s ease',
      }}
      {...props}
    >
      <TimelineEventHeader auditResult={auditResult} />
      <TimelineEventBlock auditResult={auditResult} />
      {showProjectInfo && (
        <TimelineLinkBlock
          title={localizedStrings.project}
          text={auditResult.project.name}
          href={PageUrls.project(
            auditResult.organisation?.id,
            auditResult.project.id,
          )}
        />
      )}

      {auditResult.isLikedToPermit() && (
        <TimelineLinkBlock
          title={localizedStrings.permit}
          href={
            scope === 'PROJECT'
              ? PageUrls.projectPermitDetail({
                  organisationId: auditResult.organisation?.id,
                  projectId: auditResult.project?.id,
                  permitId: auditResult.metadata?.linkedToPermit?.id,
                })
              : PageUrls.permit(
                  auditResult.organisation?.id,
                  auditResult.metadata?.linkedToPermit?.id,
                )
          }
          text={auditResult.metadata?.linkedToPermit?.template?.name}
          subText={auditResult.metadata?.linkedToPermit?.summary}
        />
      )}

      {showChecklistInfo && (
        <TimelineLinkBlock
          title={localizedStrings.checklist}
          href={
            scope === 'PROJECT'
              ? PageUrls.projectChecklistDetail({
                  organisationId: auditResult.organisation?.id,
                  projectId: auditResult.project?.id,
                  checklistId: auditResult.checklist.id,
                })
              : PageUrls.checklist(
                  auditResult.organisation?.id,
                  auditResult.checklist.id,
                )
          }
          text={auditResult.checklist.templateName}
        />
      )}
      {showPermitInfo && (
        <TimelineLinkBlock
          title={localizedStrings.permit}
          text={`${auditResult.permit.templateName} - ${auditResult.permit.shortUUID}`}
          href={
            scope === 'PROJECT'
              ? PageUrls.projectPermitDetail({
                  organisationId: auditResult.organisation?.id,
                  projectId: auditResult.project?.id,
                  permitId: auditResult.permit.id,
                })
              : PageUrls.permit(
                  auditResult.organisation?.id,
                  auditResult.permit.id,
                )
          }
        />
      )}
      {auditResult.siteboxDocument && (
        <TimelineLinkBlock
          title={localizedStrings.siteboxDocument}
          text={auditResult.siteboxDocument.name}
          href={PageUrls.projectSiteBoxDocument({
            organisationId: auditResult.organisation?.id,
            projectId: auditResult.project?.id,
            documentId: auditResult.siteboxDocument.id,
            folderId: auditResult.metadata.movedToFolder?.id || null,
          })}
        />
      )}
      {auditResult.siteboxDocumentVersion && (
        <TimelineTextBlock
          title={localizedStrings.siteBoxDocumentVersion}
          text={`Version ${auditResult.siteboxDocumentVersion.documentVersion}`}
        />
      )}
      <TimelineUserBlocks auditResult={auditResult} />
      <TimelinePermitBlocks auditResult={auditResult} />
      <TimelineOrganisationFeatureFlagsBlock auditResult={auditResult} />
      {auditResult.isBriefingParticipant() && (
        <TimelineBriefingParticipantBlock auditResult={auditResult} />
      )}
      {(auditResult.isBriefingSession() ||
        auditResult.isAddParticipantsAction()) && (
        <TimelineBriefingSessionBlock auditResult={auditResult} />
      )}
      {auditResult.isCheckInAction() && (
        <TimelineCheckInBlock auditResult={auditResult} />
      )}
      {auditResult?.hasLocationAccuracy() && (
        <TimelineTextBlock
          title={localizedStrings.locationAccuracy}
          text={auditResult?.locationAccuracyText()}
        />
      )}
      {auditResult.hasComment() && (
        <TimelineChecklistCommentBlocks auditResult={auditResult} />
      )}
      {showRegisterInfo && (
        <TimelineTextBlock
          title={localizedStrings.register}
          text={auditResult.register.name}
        />
      )}
      {auditResult.hasInspection() && (
        <TimelineLinkBlock
          title={localizedStrings.inspectionInformation}
          href={PageUrls.projectInspection({
            organisationId: auditResult.organisation?.id,
            projectId: auditResult.project?.id,
            checklistId: auditResult.metadata.inspectionChecklist.checklistId,
          })}
          text={auditResult.metadata.inspectionChecklist.templateName}
        />
      )}
      {auditResult.group && scope !== 'AUTH_GROUP' && (
        <TimelineLinkBlock
          title={localizedStrings.authorisationGroup}
          href={
            auditResult.project
              ? PageUrls.projectAuthorisationGroup({
                  organisationId: auditResult.organisation?.id,
                  projectId: auditResult.project?.id,
                  groupId: auditResult.group?.id,
                })
              : PageUrls.organisationAuthorisationGroup({
                  organisationId: auditResult.organisation?.id,
                  groupId: auditResult.group.id,
                })
          }
          text={auditResult.group.name}
        />
      )}
      {showAppliedToGroups && (
        <TimelineMultiLinksBlock
          title={`${localizedStrings.authorisationGroup}${
            auditResult.appliedToGroups.length > 1 ? 's' : ''
          }`}
          links={auditResult.appliedToGroups.map((group) => ({
            href: auditResult.project
              ? PageUrls.projectAuthorisationGroup({
                  organisationId: auditResult.organisation?.id,
                  projectId: auditResult.project?.id,
                  groupId: group.id,
                })
              : PageUrls.organisationAuthorisationGroup({
                  organisationId: auditResult.organisation?.id,
                  groupId: group.id,
                }),
            text: group.name,
          }))}
        />
      )}
      {/*We hide the info here when auditResult.hasComment(). The Comment block will render the info with comment styles*/}
      {!!auditResult.info &&
        !auditResult.hasComment() &&
        !auditResult.isBriefingParticipant() && (
          <TimelineTextBlock
            title={auditResult.getInfoTitle()}
            text={auditResult.info}
          />
        )}
      {auditResult.hasTemplateInformation() && (
        <TimelineTemplateBlocks auditResult={auditResult} />
      )}
      {!!auditResult.isUpdatingRegisterItem() && (
        <TimelineRegisterPropertiesBlocks auditResult={auditResult} />
      )}
      {auditResult?.isAction('TASK_REASSIGNED') && (
        <>
          <TimelineTextBlock
            title={localizedStrings.reassignTaskNote}
            text={auditResult.metadata.taskReassignedNotes}
          />
          <TimelineDiffBlock
            title={localizedStrings.task}
            preValues={auditResult?.metadata?.taskPreValuesData}
            postValues={auditResult?.metadata?.taskPostValuesData}
            trackedFields={[
              {
                path: ['assigneeUser', 'email'],
                displayName: localizedStrings.assignTo,
                composePaths: [
                  ['assigneeUser', 'firstName'],
                  ['assigneeUser', 'lastName'],
                ],
                composeValue: ([firstName, lastName]) =>
                  firstName && lastName
                    ? `${firstName} ${lastName}`
                    : localizedStrings.none,
              },
            ]}
          />
        </>
      )}
      {auditResult?.isAction('TASK_CHANGES_REQUESTED') && (
        <TimelineTextBlock
          title={localizedStrings.changesRequested}
          text={auditResult?.metadata?.taskRequestChangesComment}
        />
      )}
      {(auditResult?.isAction('ORGANISATION_COMPANY_CREATED') ||
        auditResult?.isAction('ORGANISATION_COMPANY_MERGED') ||
        auditResult?.isAction('ORGANISATION_COMPANY_DELETED')) && (
        <TimelineCompanyBlock
          metadata={auditResult.metadata}
          title={
            auditResult?.isAction('ORGANISATION_COMPANY_MERGED')
              ? localizedStrings.selectedCompany
              : localizedStrings.companyName
          }
          companyData={
            auditResult?.isAction('ORGANISATION_COMPANY_CREATED')
              ? auditResult.metadata.postCompanyDataValues
              : auditResult.metadata.preCompanyDataValues
          }
        />
      )}
      {auditResult?.isAction('ORGANISATION_COMPANY_UPDATED') && (
        <TimelineCompanyDiffBlock metadata={auditResult.metadata} />
      )}
      {auditResult?.isAction('ORGANISATION_COMPANY_MERGED') && (
        <TimelineCompanyBlock
          metadata={auditResult.metadata}
          title={localizedStrings.mergeIntoCompany}
          companyData={auditResult.metadata.postCompanyDataValues}
        />
      )}
      {auditResult?.isAction('COMPANY_ONBOARDING_USER_ASSIGNED') &&
        !!auditResult.metadata?.orgRole && (
          <TimelineTextBlock
            title={localizedStrings.role}
            text={OrganisationUser.roleString(auditResult.metadata.orgRole)}
          />
        )}
      {auditResult?.isAction('COMPANY_ONBOARDING_RESUBMITTED') && (
        <TimelineOnboardingFormDiffBlock
          preFormData={auditResult.metadata?.preOnboardingFormData}
          postFormData={auditResult.metadata?.postOnboardingFormData}
        />
      )}
      {auditResult?.isComplianceDetailEvent() && (
        <TimelineComplianceEventBlock auditResult={auditResult} />
      )}
      {(auditResult?.isAction('USER_COMPETENCY_COMPLETED') ||
        auditResult?.isAction('USER_COMPETENCY_EXPIRED')) &&
        !!auditResult.metadata?.competencyTitle && (
          <TimelineTextBlock
            title={localizedStrings.competency}
            text={auditResult.metadata.competencyTitle}
          />
        )}
      {auditResult.isDraftAction() && (
        <TimelineTextBlock title={draftName} text={draftTemplateName} />
      )}
      <Collapsible
        label={{
          collapsed: localizedStrings.showMore,
          open: localizedStrings.hide,
        }}
        buttonProps={{
          size: 'small',
          sx: { mt: 1, mb: -0.5 },
        }}
        collapsed
        hidden={scope === 'MAP_MODAL'}
      >
        {auditResult.isUpdatingProjectDetails() && (
          <TimelineProjectUpdatesBlock
            projectDataAfter={auditResult.metadata.projectDataAfter}
            projectDataBefore={auditResult.metadata.projectDataBefore}
          />
        )}
        {auditResult.isUpdatingSiteBoxDocumentStatus() && (
          <TimelineSiteBoxStatusTrailBlock
            preSiteBoxDocumentValuesData={
              auditResult.metadata.preSiteBoxDocumentValuesData
            }
            postSiteBoxDocumentValuesData={
              auditResult.metadata.postSiteBoxDocumentValuesData
            }
          />
        )}
        {auditResult.isRenamingSiteBoxDocument() && (
          <TimelineSiteBoxDocumentUpdateBlock
            preSiteBoxDocumentValuesData={
              auditResult.metadata.preSiteBoxDocumentValuesData
            }
            postSiteBoxDocumentValuesData={
              auditResult.metadata.postSiteBoxDocumentValuesData
            }
          />
        )}
        {auditResult.metadata?.movedToFolder && (
          <TimelineMoveToFolderBlock auditResult={auditResult} />
        )}
        {auditResult.isUpdatingTemplatePermissionGroups() && (
          <TimelinePermissionAssignmentsBlocks auditResult={auditResult} />
        )}
        {auditResult.isUpdatingProjectMemberRole() && (
          <TimelineUserRoleUpdateBlock
            preValues={auditResult.metadata.preProjectMemberData.roles.map(
              (role) => ProjectUser.getRoleString(role),
            )}
            postValues={auditResult.metadata.postProjectMemberData.roles.map(
              (role) => ProjectUser.getRoleString(role),
            )}
          />
        )}
        {auditResult.isUpdatingOrganisationMemberRole() && (
          <TimelineUserRoleUpdateBlock
            preValues={auditResult.metadata.preOrgMemberData.roles.map((role) =>
              OrganisationUser.roleString(role),
            )}
            postValues={auditResult.metadata.postOrgMemberData.roles.map(
              (role) => OrganisationUser.roleString(role),
            )}
          />
        )}
        {auditResult.metadata?.updateNote && (
          <TimelineTextBlock
            title={localizedStrings.info}
            text={auditResult.metadata.updateNote}
          />
        )}
        {auditResult.isUpdatingGroupPermissions() && (
          <TimelineGroupPermissionsBlocks
            permissionsUpdatedData={auditResult.metadata.permissionsUpdatedData}
            scope={scope}
            organisationId={auditResult.organisation?.id}
            projectId={auditResult.project?.id}
          />
        )}
        {auditResult.hasPermitStatusChange() && (
          <TimelinePermitStatusTrailBlock
            permitStatusBefore={auditResult.metadata.permitStatusBefore}
            permitStatusAfter={auditResult.metadata.permitStatusAfter}
          />
        )}
        {auditResult.hasProjectStatusChange() && (
          <TimelineProjectStatusTrailBlock
            projectStatusBefore={auditResult.metadata.projectStatusBefore}
            projectStatusAfter={auditResult.metadata.projectStatusAfter}
          />
        )}
        {auditResult.getCoordinates() && (
          <TimelinePermitLocationBlock
            auditResult={auditResult}
            auditResults={auditResults}
          />
        )}
        {auditResult.hasProofs() && (
          <TimelineMediaBlock auditResult={auditResult} />
        )}

        {auditResult.isTaskAction() &&
          auditResult.metadata?.taskPostValuesData?.stepData?.hasAnswers() && (
            <TimelineTaskStepQuestionsBlock auditResult={auditResult} />
          )}

        {auditResult?.isAction('TASK_WORK_STEP_SUBMITTED') && (
          <>
            <TimelineDiffBlock
              title={localizedStrings.task}
              preValues={auditResult?.metadata?.taskPreValuesData}
              postValues={auditResult?.metadata?.taskPostValuesData}
              trackedFields={[
                {
                  path: ['assigneeUser', 'email'], // Main path for change detection
                  displayName: localizedStrings.assignTo,
                  composePaths: [
                    ['assigneeUser', 'firstName'],
                    ['assigneeUser', 'lastName'],
                  ],
                  composeValue: ([firstName, lastName]) =>
                    firstName && lastName
                      ? `${firstName} ${lastName}`
                      : localizedStrings.none,
                },
                {
                  path: ['status'],
                  displayName: localizedStrings.status,
                  formatValue: (value) =>
                    capitalize(value?.toLowerCase().replace(/_/g, ' ')),
                },
                {
                  path: ['currentStep', 'name'],
                  displayName: localizedStrings.stepName,
                },
              ]}
            />
          </>
        )}

        {auditResult?.isAction('TASK_APPROVED') && (
          <TimelineDiffBlock
            title={localizedStrings.task}
            preValues={auditResult?.metadata?.taskPreValuesData}
            postValues={auditResult?.metadata?.taskPostValuesData}
            trackedFields={[
              {
                path: ['assigneeUser', 'email'], // Main path for change detection
                displayName: localizedStrings.assignTo,
                composePaths: [
                  ['assigneeUser', 'firstName'],
                  ['assigneeUser', 'lastName'],
                ],
                composeValue: ([firstName, lastName]) =>
                  firstName && lastName
                    ? `${firstName} ${lastName}`
                    : localizedStrings.none,
              },
              {
                path: ['status'],
                displayName: localizedStrings.status,
                formatValue: (value) =>
                  capitalize(value?.toLowerCase().replace(/_/g, ' ')),
              },
              {
                path: ['currentStep', 'name'],
                displayName: localizedStrings.stepName,
              },
            ]}
          />
        )}

        {auditResult?.isAction('TASK_UPDATED') && (
          <TimelineDiffBlock
            title={localizedStrings.task}
            preValues={auditResult?.metadata?.taskPreValuesData}
            postValues={auditResult?.metadata?.taskPostValuesData}
            trackedFields={[
              {
                path: ['dueDate'],
              },
              {
                path: ['summary'],
              },
              {
                path: ['priority'],
                formatValue: (value) =>
                  capitalize(value?.toLowerCase().replace(/_/g, ' ')),
              },
            ]}
          />
        )}

        {auditResult?.isAction('TASK_CANCELLED') && (
          <>
            <TimelineTextBlock
              title={localizedStrings.note}
              text={auditResult.metadata.taskCancelledNotes}
            />
            <TimelineDiffBlock
              title={localizedStrings.task}
              preValues={auditResult?.metadata?.taskPreValuesData}
              postValues={auditResult?.metadata?.taskPostValuesData}
              trackedFields={[
                {
                  path: ['status'],
                  displayName: localizedStrings.status,
                  formatValue: (value) =>
                    capitalize(value?.toLowerCase().replace(/_/g, ' ')),
                },
              ]}
            />
          </>
        )}

        {auditResult?.isAction('TASK_REOPENED') && (
          <>
            {auditResult.metadata?.taskReopenReason && (
              <TimelineTextBlock
                title={localizedStrings.reason}
                text={auditResult.metadata.taskReopenReason}
              />
            )}
            <TimelineDiffBlock
              title={localizedStrings.task}
              preValues={auditResult?.metadata?.taskPreValuesData}
              postValues={auditResult?.metadata?.taskPostValuesData}
              trackedFields={[
                {
                  path: ['currentStep', 'name'],
                  displayName: localizedStrings.stepName,
                },
                {
                  path: ['status'],
                  displayName: localizedStrings.status,
                  formatValue: (value) =>
                    capitalize(value?.toLowerCase().replace(/_/g, ' ')),
                },
                {
                  path: ['assigneeUser', 'email'], // Main path for change detection
                  displayName: localizedStrings.assignTo,
                  composePaths: [
                    ['assigneeUser', 'firstName'],
                    ['assigneeUser', 'lastName'],
                  ],
                  composeValue: ([firstName, lastName]) =>
                    firstName && lastName
                      ? `${firstName} ${lastName}`
                      : localizedStrings.none,
                },
              ]}
            />
          </>
        )}

        {auditResult?.isAction('TASK_CHANGES_REQUESTED') && (
          <>
            <TimelineDiffBlock
              title={localizedStrings.task}
              preValues={auditResult?.metadata?.taskPreValuesData}
              postValues={auditResult?.metadata?.taskPostValuesData}
              trackedFields={[
                {
                  path: ['currentStep', 'name'],
                  displayName: localizedStrings.stepName,
                },
                {
                  path: ['status'],
                  displayName: localizedStrings.status,
                  formatValue: (value) =>
                    capitalize(value?.toLowerCase().replace(/_/g, ' ')),
                },
              ]}
            />
          </>
        )}

        {!!auditResult.signatureUrl &&
          !auditResult.isTaskAction() &&
          !auditResult.isBriefingAction() && (
            <TimelineSignatureBlock auditResult={auditResult} />
          )}
      </Collapsible>
    </Card>
  )
}
